"use client";
import React, { useState, useMemo } from "react";
import CalculatorSliderField from "../charts/CalculatorSliderField";
import { FundNavSeries, SipReturnResult } from "@/type/comparePageTypes";
import { calculateSipReturnsForFunds } from "@/utils/comparePageFunctions";
import { formatCompactNumber, formatIndianNumber } from "@/utils/commonFunctions";
import { useLoginModal } from "@/utils/useLoginModal";
import Image from "next/image";

function sortByGainLossPercent(data: SipReturnResult[], order: "asc" | "desc" = "desc"): SipReturnResult[] {
  return [...data].sort((a, b) => {
    const aValue = a.gainLossPercent ?? 0;
    const bValue = b.gainLossPercent ?? 0;

    return order === "asc" ? aValue - bValue : bValue - aValue;
  });
}

function getNormalizedWidth(gainLossPercent: number, boost: number = 10): number {
  if (gainLossPercent <= 0) return 0;

  if (gainLossPercent < 100) {
    return Math.min(gainLossPercent, 100);
  }

  return (gainLossPercent / (gainLossPercent + boost)) * 100;
}

export default function CompareSIPCalculator({ fundsNav }: { fundsNav: FundNavSeries[] }) {
  const [monthlyInvestment, setMonthlyInvestment] = useState<number>(50000);
  const [tenure, setTenure] = useState<number>(5);

  const result = useMemo(() => {
    return calculateSipReturnsForFunds({ fundsNav: fundsNav, monthlyInvestment, tenureYears: tenure });
  }, [fundsNav, monthlyInvestment, tenure]);

  const { isLoggedIn, open, setModalKey } = useLoginModal();

  const dummySipResult: SipReturnResult[] = [
    {
      fundName: "Login to see premium fund details",
      gainLossPercent: 60,
      currentValue: 0,
      annualizedReturn: 0,
      investedAmount: monthlyInvestment,
      gainLoss: 0,
      fundAgeInYears: "1Y 1M",
    },
    {
      fundName: "Premium Fund Performance Hidden",
      gainLossPercent: 40,
      currentValue: 0,
      annualizedReturn: 0,
      investedAmount: 0,
      gainLoss: 0,
      fundAgeInYears: "1Y 1M",
    },
    {
      fundName: "Unlock to view SIP projections",
      gainLossPercent: 25,
      currentValue: 0,
      annualizedReturn: 0,
      investedAmount: 0,
      gainLoss: 0,
      fundAgeInYears: "1Y 1M",
    },
    {
      fundName: "Unlock to view SIP projections",
      gainLossPercent: 25,
      currentValue: 0,
      annualizedReturn: 0,
      investedAmount: 0,
      gainLoss: 0,
      fundAgeInYears: "1Y 1M",
    },
  ];

  const sipResult = useMemo(() => {
    if (!isLoggedIn) {
      return sortByGainLossPercent(dummySipResult);
    }

    return sortByGainLossPercent(result);
  }, [isLoggedIn, result]);

  return (
    <div className="w-full mx-auto max-w-210 border border-slate-200 rounded-2xl sm:rounded-8xl bg-white shadow-[0px_8px_10px_-6px_rgba(0,0,0,0.1),0px_20px_25px_-5px_rgba(0,0,0,0.1),0px_0px_0px_1px_#F1F5F9] overflow-hidden">
      <div className="flex flex-col xs:flex-row items-center gap-10 xs:gap-8 p-4 sm:p-6 mb-5">
        <CalculatorSliderField
          config={{
            label: "MONTHLY INVESTMENT",
            prefix: "₹ ",
            min: 100,
            max: 500000,
            step: 100,
          }}
          value={monthlyInvestment}
          onChange={setMonthlyInvestment}
        />

        <CalculatorSliderField
          config={{
            label: "TENURE",
            suffix: " Years",
            min: 1,
            max: 10,
            step: 1,
          }}
          value={tenure}
          onChange={setTenure}
        />
      </div>

      <div className="bg-linear-to-b/srgb from-[#0F264C] to-[#15386F] relative">
        <div className="p-4 xs:p-6">
          <div className="mb-3 sm:mb-6 pb-4 sm:pb-6 border-b border-dashed border-white/70">
            <span className="inline-block text-lg xs:text-xl xs:leading-7 sm:text-2xl sm:leading-8 font-semibold leading-1.1 text-white mb-2.5">Projected Wealth</span>
            <div className="flex flex-col xs:flex-row xs:items-center gap-1.5 xs:gap-4 text-2xs sm:text-base">
              <div className="text-white/50 leading-1.2 font-light">
                Investment Amount : <span className="font-medium text-white">₹ {formatIndianNumber(sipResult[0].investedAmount)}</span>
              </div>
              <span className="hidden w-1 h-1 rounded-full bg-[#8794A9] xs:inline-block" />
              <div className="text-white/50 leading-1.2 font-light">
                Tenure : <span className="font-medium text-white">{tenure} Yrs.</span>
              </div>
            </div>
          </div>

          <div className="space-y-3 sm:space-y-6">
            {sipResult.map((fund, ind) => {
              const returnValue = fund.annualizedReturn?.toFixed(2) ? Number(fund.annualizedReturn?.toFixed(2)) : 0;
              return (
                <div key={ind} className="flex items-center justify-between gap-6 sm:gap-12 text-white">
                  <div className="space-y-2 w-full">
                    <span className="flex items-center gap-1 font-semibold text-sm sm:text-base">
                      <p className="line-clamp-1">{fund.fundName}</p>
                      <span className="shrink-0 opacity-90 font-light text-2xs sm:text-2sm">{fund.fundAgeInYears ? `( Fund Age - ${fund.fundAgeInYears} )` : null}</span>
                    </span>
                    <div className={`h-1 xs:h-2 w-full rounded-full bg-white/35 overflow-hidden  ${ind !== 0 ? "opacity-30" : ""}`}>
                      <div className="h-full bg-[#00BB41] rounded-full" style={{ width: `${getNormalizedWidth(fund.gainLossPercent)}0%` }} />
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 shrink-0">
                    <span className="sm:text-xl font-semibold">₹{formatCompactNumber(fund.currentValue, true)}</span>
                    <span className={`text-2xs sm:text-base text-right font-medium ${returnValue > 0 ? "text-[#11FF29]" : "text-[#ff3e3e]"}`}>{returnValue > 0 ? `+${returnValue}` : `${returnValue}`}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="p-3 sm:p-4 bg-white/20 text-white/70 text-xs sm:text-sm text-center">
          <span className="font-semibold text-white">Note:</span> SIP value calculations are based on historical NAV data of funds.
        </div>

        {!isLoggedIn && (
          <div className="absolute inset-0 bg-black/65 backdrop-blur-md flex flex-col items-center justify-center">
            <Image src="/next_assets/images/common-images/lock-img-v2.png" alt="Lock Image" width={200} height={200} className="w-24 h-24 sm:w-28 sm:h-w-28 object-contain mb-3" />

            <div className="space-y-3 text-center max-w-2xl px-2 mb-5 sm:mb-8">
              <div className="text-white text-xl sm:text-2xl md:text-3xl font-bold">See Your Future Wealth</div>
              <p className="text-[#CAD5E2] text-sm sm:text-base md:text-lg">Unlock to compare the final corpus and find the winning fund.</p>
            </div>

            <button
              onClick={() => {
                open();
                setModalKey("compare-page-calculator");
              }}
              className="py-2.5 sm:py-3.5 w-full max-w-64 rounded-xl text-white border border-white/40 text-sm sm:text-lg font-semibold sm:font-bold bg-primaryBlue shadow-[0px_8px_10px_-6px_#0047B966,0px_20px_25px_-5px_#0047B966] shine-button-hover"
            >
              Calculate My Growth
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
